<script setup lang="ts">
import { onUnmounted, onMounted, provide, ref } from 'vue';
import OverlayAppUpdate from "@/application/components/OverlayAppUpdate.vue";
import OverlayMaintenance from "@/application/components/OverlayMaintenance.vue";
import { usePolling } from "@/application/composables/usePolling";
import { useElementSize } from '@vueuse/core'
import OverlayLockscreen from "@/application/components/OverlayLockscreen.vue";
import { hideForShallowUserClass } from "@/authentication/composables/hideForShallowUserClass.ts";

const header = ref<HTMLElement | null>(null);
const footer = ref<HTMLElement | null>(null);

const { height: headerHeight } = useElementSize(header);
const { height: footerHeight } = useElementSize(footer);

usePolling();

provide('headerHeight', headerHeight);
provide('footerHeight', footerHeight);

onMounted(() => {
  window.document.body.classList.add('mounted');
});

onUnmounted(() => {
  window.document.body.classList.remove('mounted');
})
</script>

<template>
  <header id="app-header" ref="header" :class="hideForShallowUserClass()" />
  <main id="app-main">
    <div id="app-page-head" :class="hideForShallowUserClass()" />
    <div id="app-page-content">
      <RouterView />
    </div>
    <div id="app-page-footer" :class="hideForShallowUserClass()" />
  </main>
  <footer id="app-footer" ref="footer" :class="hideForShallowUserClass()" />
  <Teleport to="#app-overlay">
    <OverlayAppUpdate />
    <OverlayMaintenance />
  </Teleport>
  <Teleport to="#app-lockscreen">
    <OverlayLockscreen />
  </Teleport>
</template>

<style>
#app {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  contain: layout style size;
  z-index: 1;
}

#app-header {
  z-index: 2;
  width: 100%;
  flex: none;
}

#app-footer {
  z-index: 2;
  background: white;
  flex: none;
  width: 100%;
  display: block;
}

#app-main {
  display: flex;
  flex: 1;
  width: 100%;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior-y: contain;
}

#app-page-head {
  flex: none;
  width: 100%;
  display: block;
}

#app-page-footer {
  flex: none;
  display: block;
  width: 100%;
}

#app-page-content {
  position: relative;
  flex: 1;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
}

#app-page-head {
  flex: none;
  display: block;
  width: 100%;
}

/*
 * Target for BaseModal (`append-to`). Rendered in place, a modal stays inside the page's
 * stacking context — on IonContent pages that is the scroll layer (`z-index: 0`), so the
 * header, the mobile menu and slotted FABs painted over it. Here it sits above all of #app
 * and below #app-overlay (media preview, maintenance, app update) and the lockscreen.
 * EP poppers (select, dropdown) still teleport to body and stay above open modals.
 */
#app-modals {
  position: relative;
  z-index: 5;
}

#app-overlay {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: fixed;
  overflow-x: hidden;
  overflow-y: auto;
  pointer-events: none;
  z-index: 10;
}

#app-lockscreen {
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  position: fixed;
  overflow: hidden;
  pointer-events: none;
  z-index: 99999;
}
</style>
